import { ReloadOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { MouseEvent as ReactMouseEvent } from 'react';

import { StatusTag } from '../../components/index';
import { HealthDependency } from '../../services/integrationHealthApi';
import { HealthHistoryStrip } from './HealthHistoryStrip';
import {
  DIRECTION_LABEL,
  MODE_LABEL,
  reasonLabel,
  STATUS_ACCENT,
  STATUS_LABEL,
  STATUS_TAG_CLASS,
} from './healthLabels';

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatLatency(latencyMs: number | null): string {
  if (latencyMs === null || latencyMs === undefined) return '—';
  if (latencyMs < 1000) return `${latencyMs} ms`;
  return `${(latencyMs / 1000).toFixed(1)} s`;
}

// Mostra a mensagem do detail quando ela agrega ao rótulo da razão (ex.:
// "Certificado A1 expira em 12 dias", "sem eventos no período"). Sem segredo:
// o backend nunca põe payload de negócio no detail.
function detailMessage(detail: Record<string, unknown>): string | null {
  const message = detail?.message;
  return typeof message === 'string' ? message : null;
}

// Card de parceiro: a saúde é log-based (volume/erro), não probe. Discrimina-se
// pela presença de volume_24h no detail, que só o grupo de parceiros traz.
function isPartnerCard(detail: Record<string, unknown>): boolean {
  return typeof detail?.volume_24h === 'number';
}

function formatErrorRate(rate: unknown): string {
  if (typeof rate !== 'number') return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

function PartnerFooter(props: {
  detail: Record<string, unknown>;
  checkedAt: string | null;
}) {
  const { detail, checkedAt } = props;
  const applications = Array.isArray(detail.applications)
    ? (detail.applications as string[])
    : [];
  const volume = (detail.volume_24h as number) ?? 0;

  return (
    <div
      style={{
        marginTop: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 11,
        color: 'var(--neutral_medium)',
        borderTop: '1px solid var(--neutral_lightest)',
        paddingTop: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span>última chamada</span>
        <span>{formatDateTime((detail.last_call_at as string) || null)}</span>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>volume 24h {volume}</span>
        {/* sem chamadas no período não tem taxa a calcular — evita o "—" que
            parecia erro escondido */}
        {volume > 0 ? (
          <span>taxa de erro {formatErrorRate(detail.error_rate)}</span>
        ) : (
          <span>sem chamadas no período</span>
        )}
      </div>
      {applications.length > 0 && (
        <div style={{ display: 'flex', gap: 8 }}>
          <span>chamado por {applications.join(', ')}</span>
        </div>
      )}
      {/* O card mais desatualizado da tela era justamente o que não dizia
          quando foi verificado: `verificado` só aparecia no ramo não-parceiro. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span>verificado</span>
        <span>{formatDateTime(checkedAt)}</span>
      </div>
    </div>
  );
}

export function DependencyCard(props: {
  dependency: HealthDependency;
  onOpenLogs: (dependency: HealthDependency) => void;
  onCheck: (serviceKey: string) => void;
  isChecking: boolean;
}) {
  const { dependency, onOpenLogs, onCheck, isChecking } = props;
  const message = detailMessage(dependency.detail);
  const accent = STATUS_ACCENT[dependency.status];
  const partner = isPartnerCard(dependency.detail);

  // Clique no card abre as últimas requisições; os botões internos param a
  // propagação para não abrir o drawer sem querer.
  const stop = (event: ReactMouseEvent) => event.stopPropagation();

  return (
    <div
      onClick={() => onOpenLogs(dependency)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onOpenLogs(dependency);
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        height: '100%',
        padding: '14px 16px',
        background: '#fff',
        border: '1px solid var(--neutral_lighter)',
        borderLeft: `4px solid ${accent}`,
        borderRadius: 8,
        boxShadow: '0 1px 2px rgba(46, 52, 57, 0.06)',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              color: 'var(--neutral_dark)',
              lineHeight: 1.3,
            }}
          >
            {dependency.label}
          </div>
          <div style={{ fontSize: 12, color: 'var(--neutral_medium)' }}>
            {dependency.owner}
          </div>
        </div>
        <StatusTag>
          <span className={STATUS_TAG_CLASS[dependency.status]}>
            {STATUS_LABEL[dependency.status]}
          </span>
        </StatusTag>
      </div>

      <div style={{ fontSize: 13, color: 'var(--neutral_dark)' }}>
        {reasonLabel(dependency.reason)}
      </div>

      {message && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--neutral_medium)',
            background: 'var(--neutral_lightest)',
            borderRadius: 6,
            padding: '6px 8px',
          }}
        >
          {message}
        </div>
      )}

      {dependency.history.length > 0 && (
        <HealthHistoryStrip
          history={dependency.history}
          stale={dependency.stale}
        />
      )}

      {partner ? (
        <PartnerFooter
          detail={dependency.detail}
          checkedAt={dependency.checked_at}
        />
      ) : (
        <div
          style={{
            marginTop: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            fontSize: 11,
            color: 'var(--neutral_medium)',
            borderTop: '1px solid var(--neutral_lightest)',
            paddingTop: 8,
          }}
        >
          <div
            style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
          >
            <Tooltip title="Como a saúde deste serviço é medida">
              <span>{MODE_LABEL[dependency.mode] || dependency.mode}</span>
            </Tooltip>
            <span>latência {formatLatency(dependency.latency_ms)}</span>
          </div>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
          >
            <span>
              {DIRECTION_LABEL[dependency.direction] || dependency.direction}
            </span>
            <span>verificado {formatDateTime(dependency.checked_at)}</span>
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {dependency.notes ? (
          <Tooltip title={dependency.notes}>
            <span
              onClick={stop}
              style={{
                fontSize: 11,
                color: 'var(--ocean_medium)',
                cursor: 'help',
              }}
            >
              entenda esta medição
            </span>
          </Tooltip>
        ) : (
          <span />
        )}
        <Button
          type="link"
          size="small"
          icon={<ReloadOutlined />}
          loading={isChecking}
          onClick={(event) => {
            stop(event);
            onCheck(dependency.key);
          }}
          style={{ padding: 0, height: 'auto', fontSize: 12 }}
        >
          verificar agora
        </Button>
      </div>
    </div>
  );
}
